import { HttpError, useApiUrl, useCustom } from "@refinedev/core";
import { useSession } from "hooks/useSession";
import { is404 } from "utils/network";
import type { CreatorProfile } from "../types";

interface UseCreatorProfileParams {
  userId?: number | null;
  workspaceId?: string;
  projectId?: string;
  enabled?: boolean;
}

/**
 * Resolves the single best-matching Creator Profile for the given user and
 * scope (priority: project > workspace > organization). A 404 (no profile, or
 * org mismatch) is treated as "no profile" rather than an error.
 */
export const useCreatorProfile = ({
  userId,
  workspaceId,
  projectId,
  enabled = true,
}: UseCreatorProfileParams) => {
  const apiUrl = useApiUrl();
  const { currentOrganization, isLoading: isSessionLoading } = useSession();
  const organizationId = currentOrganization?.id;

  const isEnabled = enabled && !!organizationId && !!userId;
  // The session finished resolving but produced no org id (its queries errored,
  // so currentOrganization fell back to the id-less default). The request will
  // never fire, so report an error instead of loading forever.
  const isSessionUnresolved = !isSessionLoading && !organizationId;

  const { data, isLoading, isError, error, refetch } = useCustom<
    CreatorProfile,
    HttpError
  >({
    url: `${apiUrl}/media/${organizationId}/creator-profiles`,
    method: "get",
    config: {
      query: {
        user_id: userId,
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
        ...(projectId ? { project_id: projectId } : {}),
      },
    },
    errorNotification: false,
    queryOptions: {
      enabled: isEnabled,
      retry: (count, err) => !is404(err) && count < 2,
    },
  });

  const notFound = is404(error);

  return {
    profile: notFound ? null : data?.data ?? null,
    // Report loading while the caller wants a result but we don't have a
    // definitive one yet — either the query is in flight, or its own
    // dependencies (org id, user id) haven't resolved so the request is still
    // disabled. Without the `!isEnabled` case, a consumer that resolves the
    // user before the org id arrives sees isLoading=false + profile=null and
    // mistakes "not asked yet" for "no profile".
    isLoading: enabled && !isSessionUnresolved && (!isEnabled || isLoading),
    isError: (isError && !notFound) || (enabled && isSessionUnresolved),
    notFound,
    refetch,
  };
};
